import { env } from "cloudflare:workers";
import { createServerFn } from "@tanstack/react-start";
import { HeadContent, Outlet, Scripts, createRootRoute } from "@tanstack/react-router";
import { Provider } from "@/components/ui/provider";
import { StandaloneNotFound, StandaloneError } from "@/components/error-page";

import appCss from "@/app.css?url";

export const getVersionMetadata = createServerFn({ method: "GET" }).handler(async () => {
  const meta = env.CF_VERSION_METADATA;
  return { id: meta.id || "dev", tag: meta.tag || "dev" };
});

const SITE_URL = "https://loonie.app";
const SITE_DESCRIPTION =
  "Split expenses with friends and groups. Track shared costs, settle debts, and manage group finances effortlessly.";

export const Route = createRootRoute({
  notFoundComponent: () => (
    <Provider>
      <StandaloneNotFound />
    </Provider>
  ),
  errorComponent: ({ error, reset }) => (
    <Provider>
      <StandaloneError error={error} reset={reset} />
    </Provider>
  ),
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Loonie — Split expenses with friends" },
      { name: "description", content: SITE_DESCRIPTION },
      { name: "theme-color", content: "#FFD700" },
      // Open Graph
      { property: "og:type", content: "website" },
      { property: "og:url", content: SITE_URL },
      { property: "og:site_name", content: "Loonie" },
      { property: "og:title", content: "Loonie — Split expenses with friends" },
      { property: "og:description", content: SITE_DESCRIPTION },
      // Twitter
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Loonie — Split expenses with friends" },
      { name: "twitter:description", content: SITE_DESCRIPTION },
      // Apple mobile web app
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "Loonie" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.svg" },
      { rel: "manifest", href: "/manifest.json" },
      { rel: "stylesheet", href: appCss },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <Provider>
      <Outlet />
    </Provider>
  );
}
